import type { FC, ReactNode } from 'react';
import React, { useState, Fragment, forwardRef } from 'react';
import { css, styled } from '@compiled/react';

import AkChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import AkChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import type { TooltipPrimitiveProps } from '@atlaskit/tooltip';
import Tooltip, { TooltipPrimitive } from '@atlaskit/tooltip';
import { token } from '@atlaskit/tokens';
import type { TriggerProps } from '@atlaskit/tooltip/types';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { Box, Text } from '@atlaskit/primitives/compiled';

import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';

import { useId, getSectionId } from './collapsibleSectionUtils.tsx';

// Needed for un-nesting afterIconButton from the header button for better accessibility
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderAreaGrid = styled.div({
	display: 'grid',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:hover > :first-of-type': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CollapsibleSectionHeader = styled.button({
	display: 'flex',
	gridRow: 1,
	gridColumn: 1,
	height: '36px',
	width: '100%',
	backgroundColor: token('elevation.surface'),
	cursor: 'pointer',
	alignItems: 'center',
	position: 'relative',
	font: token('font.heading.xxsmall'),
	color: token('color.text'),
	padding: `0px ${token('space.050')} 0px ${token('space.100')}`,
	border: 'none',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	'&:hover': {
		backgroundColor: `${token('color.background.neutral.subtle.hovered')}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NewFeaturesCollapsibleSectionHeader = styled.button({
	display: 'flex',
	gridRow: 1,
	gridColumn: 1,
	height: '36px',
	width: '100%',
	backgroundColor: token('elevation.surface'),
	alignItems: 'center',
	position: 'relative',
	font: token('font.body'),
	color: `${token('color.text')}`,
	padding: `0px ${token('space.050')} 0px 0px`,
	border: 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CollapsibleSectionWrapper = styled.div<{ isOpen: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'#page-tree-spinner': {
		minHeight: '0px',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginBottom: ({ isOpen }) => (isOpen ? '18px' : '0'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FadeInContainer = styled.div<{ isOpen: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: ({ isOpen }) => (isOpen ? '100%' : '0%'),
	transition: 'opacity 0.5s ease',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AfterIconButtonWrapper = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
	gridRow: 1,
	gridColumn: 1,
	margin: `${token('space.075')} ${token('space.050')} 0px ${token('space.050')}`,
	height: '24px',
	width: '24px',
	justifySelf: 'end',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	"button:not([data-testid='space-views-menu-group'] button, button[data-testid='create-menu-link-item'], button[data-testid='create-menu-button-item'])":
		{
			position: 'relative',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			height: '24px !important',
			width: '24px',
			minHeight: '24px',
			minWidth: '24px',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'#space-apps-collapsible-section-header-button': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		span: {
			height: '16px',
			width: '16px',
			color: `${token('color.icon.accent.gray')}`,
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'&:hover span': {
			color: `${token('color.icon')}`,
		},
	},
});

const tooltipStyles = css({
	maxWidth: '300px',
	background: `${token('color.background.neutral.bold')}`,
	color: `${token('color.text.inverse')}`,
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	paddingBlock: token('space.050'),
	paddingInline: token('space.075'),
	font: token('font.body.small'),
});

export const WiderTooltip = forwardRef<HTMLDivElement, TooltipPrimitiveProps>(function WiderTooltip(
	{ children, ...props },
	ref,
) {
	return (
		<TooltipPrimitive {...props} ref={ref} css={tooltipStyles}>
			{children}
		</TooltipPrimitive>
	);
}) as React.ForwardRefExoticComponent<TooltipPrimitiveProps>;

const beforeIconColor = token('color.icon.accent.gray');
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BeforeIconBorder = styled.div({
	position: 'absolute',
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.small'),
	width: '22px',
	height: '22px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionTitle = styled.div({
	display: 'flex',
	alignItems: 'center',
	marginLeft: token('space.100'),
	color: `${token('color.text.accent.gray')}`,
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

export const EmptySidebarCollapsibleSectionContainerWrapper = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();

	return (
		<Box
			paddingBlock="space.075"
			data-vc="empty-sidebar-collapsible-section-container"
			{...ssrPlaceholderIdProp}
		>
			<Text color="color.text.subtlest">{children}</Text>
		</Box>
	);
};

const BeforeIcon = ({ isOpen, showBorder = true }: { isOpen: boolean; showBorder?: boolean }) => (
	<Fragment>
		{showBorder && <BeforeIconBorder />}
		{isOpen ? (
			<AkChevronDownIcon label="" color={beforeIconColor} spacing="spacious" size="small" />
		) : (
			<AkChevronRightIcon label="" color={beforeIconColor} spacing="spacious" size="small" />
		)}
	</Fragment>
);

type CollapsibleHeaderProps = {
	renderAfterIconButton: (
		setHeaderExpandedState: ((newIsOpen: boolean) => void) | undefined,
	) => ReactNode;
	headerTitle: string;
	id: string;
	isOpen: boolean;
	onClick: () => void;
	tooltipProps?: TriggerProps;
	setHeaderExpandedState?: (newIsOpen: boolean) => void;
};

const CollapsibleHeader: FC<CollapsibleHeaderProps> = ({
	renderAfterIconButton,
	headerTitle,
	id,
	isOpen,
	onClick,
	tooltipProps,
	setHeaderExpandedState,
}) => (
	<HeaderAreaGrid>
		<CollapsibleSectionHeader
			{...tooltipProps}
			onClick={() => onClick()}
			id={id}
			aria-expanded={isOpen}
			aria-controls={getSectionId(id)}
			data-testid={headerTitle}
		>
			<BeforeIcon isOpen={isOpen} />
			<SectionTitle>{headerTitle}</SectionTitle>
			<FadeInContainer isOpen={isOpen} />
		</CollapsibleSectionHeader>

		<AfterIconButtonWrapper>{renderAfterIconButton(setHeaderExpandedState)}</AfterIconButtonWrapper>
	</HeaderAreaGrid>
);

type NewFeaturesCollapsibleHeaderProps = {
	headerTitle: string;
	isOpen: boolean;
	onClick: () => void;
	id: string;
	tooltipProps?: TriggerProps;
};

const NewFeaturesCollapsibleHeader: FC<NewFeaturesCollapsibleHeaderProps> = ({
	headerTitle,
	isOpen,
	id,
	onClick,
	tooltipProps,
}) => (
	<HeaderAreaGrid>
		<NewFeaturesCollapsibleSectionHeader
			{...tooltipProps}
			onClick={() => onClick()}
			id={id}
			aria-expanded={isOpen}
			aria-controls={getSectionId(id)}
		>
			<BeforeIcon isOpen={isOpen} showBorder={false} />
			<SectionTitle>{headerTitle}</SectionTitle>
			<FadeInContainer isOpen={isOpen} />
		</NewFeaturesCollapsibleSectionHeader>
	</HeaderAreaGrid>
);

type CollapsibleSectionProps = {
	renderAfterIconButton: (
		setHeaderExpandedState: ((newIsOpen: boolean) => void) | undefined,
	) => ReactNode;
	children: React.ReactNode;
	headerTitle: string;
	initialExpandedState?: boolean;
	onClick?: (isOpen: boolean) => void;
	updateExpandedState?: (isOpen: boolean) => void;
	tooltipContent?: string;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TooltipContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

export const CollapsibleSection: FC<CollapsibleSectionProps> = ({
	initialExpandedState = true,
	...props
}) => {
	const [isOpen, setIsOpen] = useState<boolean>(initialExpandedState);
	return <ControlledCollapsibleSection {...props} isOpen={isOpen} setIsOpen={setIsOpen} />;
};

export const ControlledCollapsibleSection: FC<
	Omit<CollapsibleSectionProps, 'initialExpandedState'> & {
		isOpen: boolean;
		setIsOpen: (newIsOpen: boolean) => void;
	}
> = ({
	renderAfterIconButton,
	children,
	headerTitle,
	onClick = () => {},
	updateExpandedState,
	tooltipContent,
	isOpen,
	setIsOpen,
}) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const headerId = useId();

	const setHeaderExpandedState = (newIsOpen: boolean) => {
		if (newIsOpen !== isOpen) {
			updateExpandedState?.(newIsOpen);
			setIsOpen(newIsOpen);
		}
	};

	return (
		<CollapsibleSectionWrapper
			data-vc="collapsible-section-wrapper"
			{...ssrPlaceholderIdProp}
			isOpen={isOpen}
		>
			<SpotlightTarget name="sideNavCollapsibleSectionSpotlight">
				{tooltipContent === undefined ? (
					<CollapsibleHeader
						renderAfterIconButton={renderAfterIconButton}
						headerTitle={headerTitle}
						isOpen={isOpen}
						id={headerId}
						setHeaderExpandedState={setHeaderExpandedState}
						onClick={() => {
							onClick(isOpen);
							setIsOpen(!isOpen);
						}}
					/>
				) : (
					<Tooltip
						position="top"
						component={WiderTooltip}
						content={<TooltipContainer>{tooltipContent}</TooltipContainer>}
					>
						{(tooltipProps) => (
							<CollapsibleHeader
								tooltipProps={tooltipProps}
								renderAfterIconButton={renderAfterIconButton}
								headerTitle={headerTitle}
								isOpen={isOpen}
								id={headerId}
								setHeaderExpandedState={setHeaderExpandedState}
								onClick={() => {
									onClick(isOpen);
									setIsOpen(!isOpen);
								}}
							/>
						)}
					</Tooltip>
				)}
			</SpotlightTarget>
			<FadeInContainer
				isOpen={isOpen}
				id={getSectionId(headerId)}
				role="region"
				aria-labelledby={headerId}
			>
				{isOpen && children}
			</FadeInContainer>
		</CollapsibleSectionWrapper>
	);
};

type NewFeaturesCollapsibleSectionProps = {
	headerTitle: string;
	tooltipContent?: string;
	children: React.ReactNode;
};

export const NewFeaturesCollapsibleSection: FC<NewFeaturesCollapsibleSectionProps> = ({
	headerTitle,
	tooltipContent,
	children,
}) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const headerId = useId();
	return (
		<CollapsibleSectionWrapper isOpen={isOpen}>
			<Tooltip
				position="top"
				component={WiderTooltip}
				content={<TooltipContainer>{tooltipContent}</TooltipContainer>}
			>
				{(tooltipProps) => (
					<NewFeaturesCollapsibleHeader
						tooltipProps={tooltipProps}
						headerTitle={headerTitle}
						id={headerId}
						onClick={() => {
							setIsOpen(!isOpen);
						}}
						isOpen={isOpen}
					/>
				)}
			</Tooltip>
			<FadeInContainer
				isOpen={isOpen}
				id={getSectionId(headerId)}
				role="region"
				aria-labelledby={headerId}
			>
				{isOpen && children}
			</FadeInContainer>
		</CollapsibleSectionWrapper>
	);
};
